import { batch, createSignal } from 'solid-js'

const APP_TABS = ['current', 'history', 'habits', 'measurements', 'account'] as const

/* Old tab ids that still appear in bookmarks, manifests, and the legacy layer. */
const APP_TAB_ALIASES: Record<string, AppTab> = {
   weight: 'measurements',
}

export type AppTab = (typeof APP_TABS)[number]

export type ViewPanelState = 'active' | 'entering' | 'exiting' | 'inactive'
export type ViewTransitionDirection = 'forward' | 'backward'

/* Keep in sync with --view-transition-duration in index.css. */
const VIEW_TRANSITION_DURATION_MS = 360

export type SyncBannerState = {
   visible: boolean
   message: string
   detail: string
   timestamp: string
}

const [activeTab, setActiveTabSignal] = createSignal<AppTab>('current')
const [exitingTab, setExitingTabSignal] = createSignal<AppTab | null>(null)
const [isViewTransitioning, setViewTransitioningSignal] = createSignal(false)
const [viewTransitionDirection, setViewTransitionDirectionSignal] =
   createSignal<ViewTransitionDirection>('forward')

let viewTransitionTimerId: ReturnType<typeof setTimeout> | undefined
const [isTabMenuCollapsed, setTabMenuCollapsedSignal] = createSignal(true)
const [isExistingSyncKeyModalOpen, setExistingSyncKeyModalOpenSignal] =
   createSignal(false)
const [syncBannerState, setSyncBannerState] = createSignal<SyncBannerState>({
   visible: false,
   message: '',
   detail: '',
   timestamp: '',
})

/**
 * Normalizes a value to a valid app tab, defaulting to `current`.
 *
 * @param value - The candidate tab.
 * @returns The normalized tab.
 */
export function normalizeAppTab(value?: string | null): AppTab {
   if (APP_TABS.includes(value as AppTab)) {
      return value as AppTab
   }
   if (typeof value === 'string' && APP_TAB_ALIASES[value]) {
      return APP_TAB_ALIASES[value]
   }
   return 'current'
}

/**
 * Returns the adjacent tab in the ordered navigation.
 *
 * @param tab - The starting tab.
 * @param direction - The direction to move in.
 * @returns The adjacent tab, or null at the edge.
 */
export function getAdjacentAppTab(
   tab: AppTab,
   direction: ViewTransitionDirection,
): AppTab | null {
   const tabIndex = APP_TABS.indexOf(tab)
   const nextIndex = direction === 'forward' ? tabIndex + 1 : tabIndex - 1
   return APP_TABS[nextIndex] ?? null
}

/**
 * Resolves directional motion between ordered tabs.
 *
 * @param previous - The currently active tab.
 * @param next - The tab being activated.
 * @returns The direction for the transition.
 */
export function getAppTabTransitionDirection(
   previous: AppTab,
   next: AppTab,
): ViewTransitionDirection {
   return APP_TABS.indexOf(next) > APP_TABS.indexOf(previous) ? 'forward' : 'backward'
}

/**
 * Checks whether the user prefers reduced motion.
 *
 * @returns True when reduced motion is requested.
 */
export function prefersReducedMotion(): boolean {
   return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
   )
}

/**
 * Sets the active app tab, running the view transition on change.
 *
 * @param value - The tab to activate.
 * @param options - Set `animate: false` to switch without the transition.
 */
export function setActiveTab(
   value?: string | null,
   options?: { animate?: boolean; direction?: ViewTransitionDirection },
) {
   const next = normalizeAppTab(value)
   const previous = activeTab()
   if (next === previous) {
      return
   }

   clearTimeout(viewTransitionTimerId)
   const animate = (options?.animate ?? true) && !prefersReducedMotion()
   const direction = options?.direction ?? getAppTabTransitionDirection(previous, next)
   batch(() => {
      setViewTransitionDirectionSignal(direction)
      setExitingTabSignal(animate ? previous : null)
      setViewTransitioningSignal(animate)
      setActiveTabSignal(next)
   })
   if (animate) {
      viewTransitionTimerId = setTimeout(() => {
         batch(() => {
            setExitingTabSignal(null)
            setViewTransitioningSignal(false)
         })
      }, VIEW_TRANSITION_DURATION_MS)
   }
}

/**
 * Resolves the transition state for a tab's view panel.
 *
 * @param tab - The tab the panel belongs to.
 * @returns The panel state driving the view-transition CSS.
 */
export function viewPanelState(tab: AppTab): ViewPanelState {
   if (isViewTransitioning()) {
      if (tab === activeTab()) {
         return 'entering'
      }
      if (tab === exitingTab()) {
         return 'exiting'
      }
      return 'inactive'
   }
   return tab === activeTab() ? 'active' : 'inactive'
}

/**
 * Sets whether the tab menu is collapsed.
 *
 * @param collapsed - Whether the menu is collapsed.
 */
export function setTabMenuCollapsed(collapsed: boolean) {
   setTabMenuCollapsedSignal(collapsed)
}

/**
 * Toggles the tab menu collapsed state.
 */
export function toggleTabMenuCollapsed() {
   setTabMenuCollapsedSignal(!isTabMenuCollapsed())
}

/**
 * Opens the existing-sync-key modal.
 */
export function openExistingSyncKeyModal() {
   setExistingSyncKeyModalOpenSignal(true)
}

/**
 * Closes the existing-sync-key modal.
 */
export function closeExistingSyncKeyModal() {
   setExistingSyncKeyModalOpenSignal(false)
}

/**
 * Shows the sync status banner with the given message and details.
 *
 * @param next - The banner message, detail, and timestamp.
 */
export function showSyncBanner(next: Omit<SyncBannerState, 'visible'>) {
   const message = typeof next.message === 'string' ? next.message : ''
   const detail = typeof next.detail === 'string' ? next.detail : ''
   const timestamp = typeof next.timestamp === 'string' ? next.timestamp : ''
   setSyncBannerState({
      visible: true,
      message,
      detail,
      timestamp,
   })
}

/**
 * Hides and clears the sync status banner.
 */
export function hideSyncBanner() {
   setSyncBannerState({
      visible: false,
      message: '',
      detail: '',
      timestamp: '',
   })
}

export {
   activeTab,
   isExistingSyncKeyModalOpen,
   isTabMenuCollapsed,
   isViewTransitioning,
   syncBannerState,
   viewTransitionDirection,
}
